import {
  abweichenderRechnungsempfaengerPflichtfelderSchema,
  abweichenderRechnungsempfaengerSchema,
  AdresseFormValue,
  adressePflichtfelderSchema,
  adresseSchema,
  privatpersonGeburtsdatumSchema,
  PrivatUnternehmenFormValue,
  privatUnternehmenKontaktdatenSchema,
  privatUnternehmenPflichtfelderSchema,
  RechnungsempfaengerFormValue,
  unternehmenskennungenSchema,
  UnterschriftFormValue,
  VertragspartnerMode,
} from './common-vertragspartner.schema';
import { apply, applyWhen, applyWhenValue, schema, validate } from '@angular/forms/signals';
import { CustomValidators } from '@shared-ikp/utils/validators';
import { validateOptionalWithControlValidator, validateRequired, validateWithControlValidator } from '@shared-ikp/utils/signal-forms.util';

export interface NeuerVertragspartnerFormValue extends UnterschriftFormValue {
  neuerVertragspartnerArt: VertragspartnerMode | null;
  verwalteterVertragspartner: VerwalteterVertragspartnerFormValue;
  accountVertragspartner: AccountVertragspartnerFormValue;
  mieterEigentuemer: PrivatUnternehmenFormValue | null;
}

export interface AccountVertragspartnerFormValue {
  name: string;
  kundennummer: string;
  adresse: AdresseFormValue;
  uid: string;
  firmenbuchnummer: string;
  rechnungsempfaenger: RechnungsempfaengerFormValue;
}

export interface VerwalteterVertragspartnerFormValue {
  name?: string;
  kundennummer?: string;
  firmenbuchnummer?: string;
  uid?: string;
  adresse?: AdresseFormValue;
  email?: string;
  telefon?: string;
  rechnungsempfaenger: RechnungsempfaengerFormValue;
}

const accountVertragspartnerSchema = schema<AccountVertragspartnerFormValue>(path => {
  apply(path, unternehmenskennungenSchema);
  apply(path.rechnungsempfaenger, abweichenderRechnungsempfaengerSchema);
});

const selectedRechnungsempfaengerSchema = schema<RechnungsempfaengerFormValue>(path => {
  apply(path, unternehmenskennungenSchema);
});

const verwalteterVertragspartnerSchema = schema<VerwalteterVertragspartnerFormValue>(path => {
  validate(path.name, validateOptionalWithControlValidator(CustomValidators.companyNameValidator, 'contact'));
  validate(path.kundennummer, validateWithControlValidator(CustomValidators.optionalCustomerNumberValidator, 'contact'));
  validate(path.uid, validateWithControlValidator(CustomValidators.optionalUidValidator, 'contact'));
  validate(path.firmenbuchnummer, validateWithControlValidator(CustomValidators.optionalFirmenbuchNrValidator, 'contact'));
  validate(path.email, validateWithControlValidator(CustomValidators.optionalEmailValidator, 'contact'));
  validate(path.telefon, validateWithControlValidator(CustomValidators.optionalPhoneNumberValidator, 'contact'));
  applyWhenValue(path.adresse, value => value !== undefined, adresseSchema);
  applyWhen(path.rechnungsempfaenger, ctx => Boolean(ctx.value().kundennummer.trim()), selectedRechnungsempfaengerSchema);
});

const mieterEigentuemerSchema = schema<PrivatUnternehmenFormValue>(path => {
  apply(path, privatUnternehmenKontaktdatenSchema);
  applyWhen(
    path.privatpersonKontaktdaten,
    ctx => ctx.valueOf(path.vertragspartnerTyp) === 'privat' && ctx.value() !== null,
    privatpersonGeburtsdatumSchema,
  );
  apply(path.rechnungsempfaenger, abweichenderRechnungsempfaengerSchema);
});

export const neuerVertragspartnerInaktiverZaehlpunktSchema = schema<NeuerVertragspartnerFormValue>(path => {
  applyWhen(
    path.verwalteterVertragspartner,
    ctx => ctx.valueOf(path.neuerVertragspartnerArt) === VertragspartnerMode.VERWALTETER_KUNDE,
    verwalteterVertragspartnerSchema,
  );
  applyWhen(
    path.accountVertragspartner,
    ctx => ctx.valueOf(path.neuerVertragspartnerArt) === VertragspartnerMode.ACCOUNT_KUNDE,
    accountVertragspartnerSchema,
  );
  applyWhen(
    path.mieterEigentuemer,
    ctx => ctx.valueOf(path.neuerVertragspartnerArt) === VertragspartnerMode.BEARBEITBAR && ctx.value() !== null,
    mieterEigentuemerSchema,
  );
});

export const neuerVertragspartnerAktiverZaehlpunktSchema = schema<NeuerVertragspartnerFormValue>(path => {
  applyWhen(path.mieterEigentuemer, ctx => ctx.value() !== null, mieterEigentuemerSchema);
});

/** A verwalteter Vertragspartner is either picked from the list (Kundennummer set) or entered by hand. */
const verwalteterVertragspartnerPflichtfelderSchema = schema<VerwalteterVertragspartnerFormValue>(path => {
  applyWhen(
    path,
    ctx => !ctx.value().kundennummer?.trim(),
    manuellerVertragspartnerPath => {
      validate(manuellerVertragspartnerPath.name, validateRequired('inputErrors.blank.companyName'));
      applyWhenValue(manuellerVertragspartnerPath.adresse, value => value !== undefined, adressePflichtfelderSchema);
    },
  );
});

const accountVertragspartnerPflichtfelderSchema = schema<AccountVertragspartnerFormValue>(path => {
  validate(path.kundennummer, validateRequired('ummeldung.step.newContractPartner.companySelectionRequired'));
  apply(path.rechnungsempfaenger, abweichenderRechnungsempfaengerPflichtfelderSchema);
});

const mieterEigentuemerPflichtfelderSchema = schema<PrivatUnternehmenFormValue>(path => {
  apply(path, privatUnternehmenPflichtfelderSchema);
  applyWhen(
    path.privatpersonKontaktdaten,
    ctx => ctx.valueOf(path.vertragspartnerTyp) === 'privat' && ctx.value() !== null,
    privatpersonPath => validate(privatpersonPath.geburtsdatum, validateRequired('inputErrors.blank.datePicker')),
  );
  apply(path.rechnungsempfaenger, abweichenderRechnungsempfaengerPflichtfelderSchema);
});

export const neuerVertragspartnerInaktiverZaehlpunktPflichtfelderSchema = schema<NeuerVertragspartnerFormValue>(path => {
  applyWhen(
    path.verwalteterVertragspartner,
    ctx => ctx.valueOf(path.neuerVertragspartnerArt) === VertragspartnerMode.VERWALTETER_KUNDE,
    verwalteterVertragspartnerPflichtfelderSchema,
  );
  applyWhen(
    path.accountVertragspartner,
    ctx => ctx.valueOf(path.neuerVertragspartnerArt) === VertragspartnerMode.ACCOUNT_KUNDE,
    accountVertragspartnerPflichtfelderSchema,
  );
  applyWhen(
    path.mieterEigentuemer,
    ctx => ctx.valueOf(path.neuerVertragspartnerArt) === VertragspartnerMode.BEARBEITBAR && ctx.value() !== null,
    mieterEigentuemerPflichtfelderSchema,
  );
});

export const neuerVertragspartnerAktiverZaehlpunktPflichtfelderSchema = schema<NeuerVertragspartnerFormValue>(path => {
  applyWhen(path.mieterEigentuemer, ctx => ctx.value() !== null, mieterEigentuemerPflichtfelderSchema);
});
